import { signIn, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import { Icon } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import { Form, Password } from 'components/form';
import { Col, Row } from 'components/layout';

import { useNotification } from 'hooks/useNotification';
import { API_ROUTES } from 'lib/api-routes';
import { signUpRecruiterState } from 'lib/atoms/signUpRecruiterAtom';
import { useApp } from 'lib/contexts/save-in-app-context';
import { CompanyResponse } from 'lib/models/company';
import { TUserApiResponse } from 'lib/models/User.model';
import { useAuth } from 'lib/providers/AuthProvider';
import { post } from 'lib/utils/http';
import { parseErrorResponse } from 'lib/utils/parser';
import { REGEX } from 'lib/utils/regex';

import Footer from '../Footer';
import Header from '../Header';
import StepHeading from '../StepHeading';
import styles from './Step1.module.scss';

interface Step1Props {
  progress: number;
  setStep: (step: number) => void;
}

interface Inputs {
  email: string;
  password: string;
}

interface RecruiterSocialUserResponse {
  notice: string;
  company: CompanyResponse;
  user: TUserApiResponse;
}

export const Step1: React.FC<Step1Props> = ({ progress, setStep }) => {
  const notificationInstance = useNotification();
  const { data: session, status } = useSession();
  const router = useRouter();

  const [t] = useTranslation('signup.recruiter');

  const [recruiterState, setRecruiterState] =
    useRecoilState(signUpRecruiterState);
  const { company, user } = recruiterState;

  const { utm_source = '', referrer: invitation_code } = router.query;

  const { recruiterMessage, setRecruiterMessage } = useApp();

  const { updateUserApiResponse } = useAuth();

  const getPasswordErrorArray = (password: string) => {
    const errorList = [];
    //length
    if (password.length < 8) {
      errorList.push({
        message: t('step-1.password.message-8-characters'),
        type: 'password',
      });
    }
    //uppercase
    if (!/^(?=.*[A-Z]).*$/.test(password)) {
      errorList.push({
        message: t('step-1.password.message-uppercase'),
        type: 'password',
      });
    }
    //number and symbol
    if (!/^(?=.*[0-9!@#$%^&*()_\-+=]).*$/.test(password)) {
      errorList.push({
        message: t('step-1.password.message-symbol'),
        type: 'password',
      });
    }
    return errorList;
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    watch,
    setFocus,
    setValue,
  } = useForm({
    defaultValues: {
      email: user?.email,
      password: '',
    },
  });

  const emailValue = watch('email');
  const passwordValue = watch('password');

  const [isManualEmail, setIsManualEmail] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<
    { message: string; type?: string }[]
  >([]);

  const [showError, setShowError] = useState<string>('');

  useEffect(() => {
    user?.email && isManualEmail ? setFocus('password') : setFocus('email');
  }, [setFocus]);

  React.useEffect(() => {
    if (!passwordValue && !isDirty) {
      setPasswordError([]);
      return;
    }
    const errorList = getPasswordErrorArray(passwordValue);
    if (errorList.length > 0) {
      setPasswordError(errorList);
      return;
    } else {
      setPasswordError([]);
    }
  }, [passwordValue]);

  const onGoogleLogin = async () => {
    const callbackUrl = router.asPath;
    await signIn('google', {
      callbackUrl,
    });
  };

  const isLoggedInViaGoogle = session && session.oauth_provider === 'google';

  useEffect(() => {
    if (status === 'authenticated' && isLoggedInViaGoogle) {
      setLoading(true);
      const { user } = session;
      const { email = '' } = user || {};
      setValue('email', email || '');

      try {
        (async () => {
          if (isLoggedInViaGoogle) {
            handleSocialLogin('google');
          }
        })();
      } catch (error) {
        const message = parseErrorResponse(error);
        setShowError(message);
        setLoading(false);
      }
    }
  }, [status, isLoggedInViaGoogle]);

  const getCompanyDetails = () => {
    const companyDetails =
      company &&
      (company.id
        ? { company_id: company.id }
        : {
            company_name: company.name,
            ...(company.website && {
              company_website: company.website,
            }),
          });

    return companyDetails || {};
  };

  const onSubmit: SubmitHandler<Inputs> = async (user) => {
    const errorList = getPasswordErrorArray(user.password);

    if (!isManualEmail) {
      handleEmailSubmission(user.email);
      return;
    }

    if (errorList.length > 0) {
      setPasswordError(errorList);
      return;
    }

    const companyDetails = getCompanyDetails();

    const body = {
      recruiter: {
        user_attributes: {
          email: user.email,
          password: user.password,
          utm_source,
        },
        ...companyDetails,
        invitation_code,
        ...recruiterMessage,
      },
    };

    try {
      const createRecruiterResponse: { company: { data: { id: string } } } =
        await post(API_ROUTES.RECRUITER_CREATE_WITH_COMPANY, body);

      if (company)
        setRecruiterState({
          ...recruiterState,
          company: {
            ...company,
            id: createRecruiterResponse?.company?.data?.id,
          },
          user: {
            email: user.email,
          },
          step: 2,
        });
      setStep(2);

      setRecruiterMessage(null);
    } catch (error) {
      const message = parseErrorResponse(error);
      setShowError(message);
    }
  };

  const handleEmailSubmission = (email: string) => {
    if (REGEX.EMAIL.test(email)) {
      setIsManualEmail(true);
    }
  };

  const handleSocialLogin = async (authProvider: string) => {
    setLoading(true);

    const companyDetails = getCompanyDetails();

    const { user, oauth_uid, id_token, access_token, image } = session || {};

    const { name, email } = user || {};
    const first_name = name ? name.split(' ')[0] : email;
    const last_name = name ? name.split(' ')[1] : email;

    const body = {
      recruiter: {
        user_attributes: {
          email: email,
          utm_source,
          oauth_provider: authProvider,
          oauth_uid,
          id_token,
          access_token,
          first_name: first_name || '',
          last_name: last_name || '',
          avatar: image,
        },
        ...companyDetails,
        invitation_code,
        ...recruiterMessage,
      },
    };

    try {
      const socialUserResponse = await post<RecruiterSocialUserResponse>(
        API_ROUTES.RECRUITER_CREATE_WITH_COMPANY,
        body
      );

      updateUserApiResponse(socialUserResponse.user);

      if (socialUserResponse.user) {
        setStep(3);
        setRecruiterMessage(null);
      } else {
        setShowError(t('not-authorized'));
      }

      setLoading(false);
    } catch (error) {
      const message = parseErrorResponse(error);
      if (message) {
        notificationInstance.handleExceptionError({
          message: message,
          name: '',
        });
      }
      setShowError(message);
      setLoading(false);
    }
  };

  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          isManualEmail ? setIsManualEmail(false) : setStep(0);
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading
          title={
            isManualEmail
              ? t('step-1.header.title.create-password')
              : t('step-1.header.title.create-account')
          }
        />
        {!isManualEmail && (
          <>
            <ButtonV3
              color="secondary"
              startIcon="icon_google"
              startIconClassName={styles.startIcon}
              isFullWidth
              className={styles.buttonGoogle}
              onClick={onGoogleLogin}
              disabled={isLoading}
            >
              {t('step-1.google-button')}
            </ButtonV3>

            <Row
              align="center"
              justify="center"
              columnGap={12}
              isFullWidthRow
              className={styles.separatorRow}
            >
              <div className={styles.separator}></div>
              <p>{t('step-1.or')}</p>
              <div className={styles.separator}></div>
            </Row>
          </>
        )}
        <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          {isManualEmail ? (
            <>
              <Row>
                <Col span={24}>
                  <Password
                    placeholder={t('step-1.password.placeholder')}
                    {...register('password', {
                      required: t('step-1.password.required'),
                    })}
                    isValid={!errors.password && !passwordError.length}
                    showEndIconOnActive
                    version={'v2'}
                    inputClassName={styles.signUpPassword}
                  />
                  {passwordError.length === 0 && !errors.password?.message && (
                    <div className={styles.passwordRequirement}>
                      {t('step-1.password.error')}
                    </div>
                  )}
                  {errors.password?.message && (
                    <div className={styles.errorMessage}>
                      {errors.password?.message}
                    </div>
                  )}
                  {passwordError &&
                    passwordError.map((error, index) => (
                      <div className={styles.errorMessage} key={index}>
                        {error.type === 'password' && (
                          <Icon
                            iconName="close"
                            size="small"
                            className={styles.icon}
                          />
                        )}
                        {error.message}
                      </div>
                    ))}
                </Col>
              </Row>
              {showError && (
                <div className={styles.errorMessage}>{showError}</div>
              )}
              <Footer>
                <ButtonV3
                  type="submit"
                  isFullWidth
                  isLoading={isSubmitting}
                  size="xlarge"
                >
                  {t('next')}
                </ButtonV3>
              </Footer>
            </>
          ) : (
            <>
              <Row className={styles.mb24}>
                <Col span={24}>
                  <InputV2
                    label=""
                    inputClassName={styles.signUpInput}
                    placeholder={t('step-1.email.placeholder')}
                    {...register('email', {
                      required: t('step-1.email.required'),
                      pattern: {
                        value: REGEX.EMAIL,
                        message: t('step-1.email.invalid'),
                      },
                    })}
                    isInvalid={!!errors.email}
                  />
                  {errors.email?.message && (
                    <div className={styles.errorMessage}>
                      {errors.email?.message}
                    </div>
                  )}
                </Col>
              </Row>
              <ButtonV3
                type="button"
                isFullWidth
                disabled={
                  !isLoading && REGEX.EMAIL.test(emailValue || '')
                    ? false
                    : true
                }
                size="xlarge"
                onClick={() => setIsManualEmail(true)}
              >
                {t('step-1.continue-with-email')}
              </ButtonV3>
            </>
          )}
        </Form>
      </div>
    </>
  );
};
